import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useEvaluation } from '../contexts/EvaluationContext';
import { useCourseContent } from '../contexts/CourseContentContext';
import {
  BookOpen,
  Clock,
  CheckCircle,
  XCircle,
  Target,
  Award,
  ArrowLeft,
  RefreshCw,
} from 'lucide-react';

const Evaluations = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { loadCourseEvaluations, getEvaluationsByCourse, loading } = useEvaluation();
  const { getCourseContent, loadCourseContents } = useCourseContent();

  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!courseId) return;
    Promise.all([
      loadCourseEvaluations(courseId),
      loadCourseContents(courseId),
    ]).then(() => setLoaded(true));
  }, [courseId, loadCourseEvaluations, loadCourseContents]);

  const evaluations = getEvaluationsByCourse(courseId);
  const courseContent = getCourseContent(courseId);

  const handleRefresh = async () => {
    setLoaded(false);
    await loadCourseEvaluations(courseId);
    setLoaded(true);
  };

  const getStatusBadge = (ev) => {
    if (ev.user_passed) {
      return (
        <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 font-medium">
          <CheckCircle className="w-3 h-3" /> Validé
        </span>
      );
    }
    if (ev.user_attempts_count > 0) {
      return (
        <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 font-medium">
          <XCircle className="w-3 h-3" /> Échoué
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 font-medium">
        Non commencé
      </span>
    );
  };

  if (loading && !loaded) {
    return (
      <div className="min-h-full bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Chargement des évaluations…</p>
        </div>
      </div>
    );
  }

  const passedCount = evaluations.filter(e => e.user_passed).length;
  const totalCount = evaluations.length;

  return (
    <div className="min-h-full bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate(courseId ? `/course/${courseId}/learn` : -1)}
            className="p-2 text-gray-500 hover:text-gray-800 dark:hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Évaluations
              {courseContent?.title && (
                <span className="text-primary"> — {courseContent.title}</span>
              )}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              Validez chaque module pour obtenir votre certificat
            </p>
          </div>
          <button
            onClick={handleRefresh}
            className="p-2 text-gray-500 hover:text-primary transition-colors"
            title="Rafraîchir"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>

        {/* Barre de progression globale */}
        {totalCount > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5 mb-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5 text-primary" />
                <span className="font-medium text-gray-900 dark:text-white">
                  Progression vers le certificat
                </span>
              </div>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {passedCount} / {totalCount} validées
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
              <div
                className="bg-primary h-3 rounded-full transition-all duration-500"
                style={{ width: totalCount > 0 ? `${Math.round((passedCount / totalCount) * 100)}%` : '0%' }}
              />
            </div>
            {passedCount === totalCount && totalCount > 0 && (
              <p className="text-green-600 dark:text-green-400 text-sm font-medium mt-2 flex items-center gap-1">
                <CheckCircle className="w-4 h-4" /> Toutes les évaluations validées — votre certificat a été émis !
              </p>
            )}
          </div>
        )}

        {/* Liste des évaluations */}
        {!loaded || evaluations.length === 0 ? (
          <div className="text-center py-16">
            <BookOpen className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Aucune évaluation disponible
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Les évaluations seront disponibles une fois publiées par l'instructeur.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {evaluations.map((ev) => {
              const maxA = ev.max_attempts;
              const attempts = ev.user_attempts_count || 0;
              const remaining = maxA ? Math.max(0, maxA - attempts) : null;
              const bestPct = ev.user_latest_attempt
                ? Math.round(Number(ev.user_latest_attempt.percentage))
                : null;

              return (
                <div
                  key={ev.id}
                  className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border transition-colors ${
                    ev.user_passed
                      ? 'border-green-200 dark:border-green-800'
                      : 'border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <Target className="w-5 h-5 text-primary flex-shrink-0" />
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                            {ev.title}
                          </h3>
                          {getStatusBadge(ev)}
                        </div>

                        {ev.description && (
                          <p className="text-gray-600 dark:text-gray-400 text-sm mb-3">
                            {ev.description}
                          </p>
                        )}

                        <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-gray-500 dark:text-gray-400">
                          <div className="flex items-center gap-1">
                            <CheckCircle className="w-4 h-4" />
                            Score requis : <span className="font-medium text-gray-700 dark:text-gray-300">{ev.passing_score}%</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <BookOpen className="w-4 h-4" />
                            {(ev.questions || []).length} question{(ev.questions || []).length !== 1 ? 's' : ''}
                          </div>
                          {ev.time_limit_minutes && (
                            <div className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {ev.time_limit_minutes} min
                            </div>
                          )}
                          {maxA && (
                            <div className="flex items-center gap-1">
                              <RefreshCw className="w-4 h-4" />
                              {attempts}/{maxA} tentatives
                            </div>
                          )}
                        </div>

                        {/* Meilleur score */}
                        {bestPct !== null && (
                          <div className="mt-3">
                            <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
                              <span>Meilleur score : {bestPct}%</span>
                              <span>Requis : {ev.passing_score}%</span>
                            </div>
                            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                              <div
                                className={`h-1.5 rounded-full transition-all ${
                                  ev.user_passed ? 'bg-green-500' : 'bg-red-500'
                                }`}
                                style={{ width: `${Math.min(100, bestPct)}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>

                      {/* CTA */}
                      <div className="flex flex-col gap-2 items-end">
                        {ev.user_passed ? (
                          <button
                            onClick={() => navigate(`/evaluation/${ev.id}/take?courseId=${courseId}`)}
                            className="text-sm px-4 py-2 border border-green-300 dark:border-green-700 text-green-700 dark:text-green-300 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20"
                          >
                            Revoir
                          </button>
                        ) : remaining === 0 ? (
                          <span className="text-xs text-red-600 dark:text-red-400">
                            Plus de tentatives disponibles
                          </span>
                        ) : (
                          <button
                            onClick={() => navigate(`/evaluation/${ev.id}/take?courseId=${courseId}`)}
                            className="text-sm px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
                          >
                            {attempts > 0 ? 'Réessayer' : 'Commencer'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Evaluations;
